/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useMemo } from "react";
import Card from "@mui/material/Card";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";

import DataTable from "examples/Tables/DataTable";
import FlagLoader from "layouts/flagloader";

import { getMyLeaveRequests, cancelMyLeaveRequest } from "api/users";

// ✅ NEW hook
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions";
import UserFilters from "layouts/interviewSelected/components/useFilters";
import { getLeaveStatusInfo } from "layouts/leavetracker/helper";
import Icon from "@mui/material/Icon";

/* 🔹 DEBOUNCE */
const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

function MyLeaveRequest({ userId: propUserId }) {
  const [leaveData, setLeaveData] = useState([]);

  const userId = propUserId || null;

  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  /* 🔹 PAGINATION STATE */
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
  });

  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm);

  /* ✅ NEW – useDropdownOptions returns lazy‑loaded options and fetch functions */
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLeaveId, setSelectedLeaveId] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  /* ----------------------------
     FETCH DATA WITH PAGINATION
  ---------------------------- */
  const fetchLeaveRequests = useCallback(
    async (isInitial = false) => {
      try {
        isInitial ? setPageLoading(true) : setTableLoading(true);

        const params = {
          ...filters,
          q: debouncedSearch,
          page: pagination.current_page,
          per_page: pagination.per_page,
        };

        Object.keys(params).forEach((k) => {
          if (!params[k]) delete params[k];
        });

        const response = await getMyLeaveRequests(params, userId);
        setLeaveData(response?.data || []);
        if (response) {
          setPagination((p) => ({
            ...p,
            current_page: response.current_page,
            last_page: response.last_page,
            total: response.total,
            per_page: response.per_page,
          }));
        }
      } finally {
        setPageLoading(false);
        setTableLoading(false);
      }
    },
    [filters, debouncedSearch, pagination.current_page, pagination.per_page, userId]
  );

  useEffect(() => {
    fetchLeaveRequests(true);
  }, []);

  useEffect(() => {
    fetchLeaveRequests(false);
  }, [filters, debouncedSearch, pagination.current_page]);

  /* 🔹 FILTER CHANGE → PAGE RESET */
  const handleFilterChange = (key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
    setPagination((p) => ({ ...p, current_page: 1 }));
  };

  const submitCancelRequest = async () => {
    if (!cancelReason.trim()) return;
    try {
      setActionLoading(selectedLeaveId);
      await cancelMyLeaveRequest(selectedLeaveId, cancelReason);
      setOpenDialog(false);
      fetchLeaveRequests(false);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
      : "N/A";


  // 🔹 Reason formatter: 6 words ke baad cut + ...
  const formatReason = (text, maxWords = 6) => {
    if (!text) return "N/A";
    const words = text.split(" ");
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(" ") + "...";
  };
  /* ================= TABLE ================= */
  const columns = useMemo(
    () => [
      { Header: "Leave Type", accessor: "leave_type" },
      { Header: "From Date", accessor: "from_date" },
      { Header: "To Date", accessor: "to_date" },
      { Header: "Days", accessor: "days" },
      { Header: "Reason", accessor: "reason" },
      { Header: "Status", accessor: "status" },
      { Header: "Actions", accessor: "actions" },
    ],
    []
  );

  const getPaginationRange = () => {
    const total = pagination.last_page;
    const current = pagination.current_page;
    const limit = 5;

    let start = Math.max(1, current - Math.floor(limit / 2));
    let end = start + limit - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - limit + 1);
    }

    const pages = [];

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total) {
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  const rows = useMemo(() => {
    return leaveData.map((r) => ({
      leave_type: r.leave_policy?.leave_name || "N/A",
      from_date: formatDate(r.from_date),
      to_date: formatDate(r.to_date),
      days: r.days,
      reason: (
        <SoftTypography
          variant="caption"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            maxWidth: "200px",   // 👈 adjust if needed (180px / 220px)
            lineHeight: 1.4,
          }}
          title={r.reason || ""} // 👈 hover pe full text
        >
          {formatReason(r.reason, 6)}
        </SoftTypography>
      ),
      status: (() => {
        const statusInfo = getLeaveStatusInfo(r.status);
        return (
          <SoftBox display="flex" alignItems="center" gap={0.5}>
            <Icon
              sx={{
                fontSize: "1rem",
                color: (theme) => theme.palette[statusInfo.color].main,
              }}
            >
              {statusInfo.icon}
            </Icon>
            <SoftTypography
              variant="button"
              fontWeight="medium"
              color={statusInfo.color}
              sx={{ textTransform: "capitalize", fontSize: "0.8rem" }}
            >
              {statusInfo.text}
            </SoftTypography>
          </SoftBox>
        );
      })(),
      actions:
        r.status === "pending" ? (
          <Tooltip title="Cancel">
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setSelectedLeaveId(r.id);
                setCancelReason("");
                setOpenDialog(true);
              }}
            >
              {actionLoading === r.id ? (
                // <CircularProgress size={16} />
                <FlagLoader />
              ) : (
                <CloseIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        ) : (
          "-"
        ),
    }));
  }, [leaveData, actionLoading]);

  if (pageLoading) {
    return (
      <SoftBox p={3} textAlign="center">
        {/* <CircularProgress /> */}
        <FlagLoader />
      </SoftBox>
    );
  }

  return (
    <Card>
      <SoftBox p={3}>
        {/* ✅ UPDATED FILTERS – with new props */}
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dropdownOptions={dropdownOptions}
          loading={dropdownLoading}
          fetchBranches={fetchBranches}
          fetchShifts={fetchShifts}
          fetchDepartments={fetchDepartments}
          fetchDesignations={fetchDesignations}
          fetchRoles={fetchRoles}
          showHistoryFilters={false} // No date filters needed for "My Leave"
        />

        <SoftTypography variant="h6" mt={3} mb={2} color="text">
          My Leave Requests ({pagination.total})
        </SoftTypography>

        {tableLoading ? (
          <SoftBox textAlign="center" py={4}>
            {/* <CircularProgress /> */}
            <FlagLoader />
          </SoftBox>
        ) : (
          <DataTable table={{ columns, rows }} entriesPerPage={false} />
        )}

        {/* PAGINATION */}
        {pagination.last_page > 1 && (
          <SoftBox mt={2} display="flex" justifyContent="center">
            <SoftPagination variant="gradient" color="info">
              {/* {Array.from({ length: pagination.last_page }, (_, i) => (
                <SoftPagination
                  key={i}
                  item
                  active={pagination.current_page === i + 1}
                  onClick={() =>
                    setPagination((p) => ({
                      ...p,
                      current_page: i + 1,
                    }))
                  }
                >
                  {i + 1}
                </SoftPagination>
              ))} */}
              {/* FIRST */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({ ...p, current_page: 1 }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                «
              </SoftPagination>

              {/* PREV */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page - 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                ‹
              </SoftPagination>

              {/* PAGE NUMBERS */}
              {getPaginationRange().map((item, i) => {
                if (item === "...") {
                  return (
                    <SoftPagination key={i} item disabled>
                      ...
                    </SoftPagination>
                  );
                }

                return (
                  <SoftPagination
                    key={i}
                    item
                    active={pagination.current_page === item}
                    onClick={() => {
                      setPagination((p) => ({
                        ...p,
                        current_page: item,
                      }));
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    {item}
                  </SoftPagination>
                );
              })}

              {/* NEXT */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page + 1,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                ›
              </SoftPagination>

              {/* LAST */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: pagination.last_page,
                  }));
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                »
              </SoftPagination>
            </SoftPagination>
          </SoftBox>
        )}
      </SoftBox>

      {/* CANCEL DIALOG */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Cancel Leave</DialogTitle>
        <DialogContent>
          <SoftInput
            label="Cancel Reason"
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={() => setOpenDialog(false)}>Close</SoftButton>
          <SoftButton color="error" onClick={submitCancelRequest}>
            Submit
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default MyLeaveRequest;
